import React, { useCallback, useEffect, useRef } from 'react'
import { Portal } from 'src/chrome/portal/portal'
import styles from '../containers/launchPage.module.scss'

type ResponsiveDialogProps = {
  isOpen: boolean
  onClose: () => void
  ariaLabel: string
  children: React.ReactNode
}

export const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
  isOpen,
  onClose,
  ariaLabel,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    const handleMouseDown = (event: MouseEvent) => {
      const panel = panelRef.current

      if (panel && !panel.contains(event.target as Node)) {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [handleClose, isOpen])

  if (!isOpen) return null

  const handleCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    handleClose()
  }

  return (
    <Portal>
      <div className={styles.dialogOverlay}>
        <div
          ref={panelRef}
          className={styles.dialogPanel}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          tabIndex={-1}
          onClick={event => event.stopPropagation()}
        >
          <button
            className={styles.dialogCloseButton}
            type="button"
            onClick={handleCloseClick}
            aria-label="Close dialog"
          >
            ×
          </button>
          {children}
        </div>
      </div>
    </Portal>
  )
}
